import { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { FileSignature, HardHat, Megaphone, Key, CircleDashed, AlertTriangle } from 'lucide-react';
import { HudCard } from '../hud';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const opts = { withCredentials: true };

/**
 * Définition des 4 jalons du cycle de vie d'un programme immobilier.
 * Les `key` correspondent aux champs persistés dans `project.general_info`.
 */
const MILESTONES = [
  { key: 'date_depot_pc',           label: 'Dépôt PC',         shortLabel: 'PC',     icon: FileSignature },
  { key: 'date_ouverture_chantier', label: 'Ouverture chantier', shortLabel: 'Chantier', icon: HardHat },
  { key: 'date_commercialisation',  label: 'Commercialisation', shortLabel: 'Vente',  icon: Megaphone },
  { key: 'date_livraison',          label: 'Livraison',        shortLabel: 'Livraison', icon: Key },
];

const parseDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtShort = (d) =>
  d ? d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
const fmtLong = (d) =>
  d ? d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : null;

/**
 * Calcule la position en pourcentage [0..100] sur la timeline pour une date donnée.
 * - 0% = date min (premier jalon renseigné)
 * - 100% = date max (dernier jalon renseigné)
 * Si moins de 2 dates renseignées, on retombe sur un fallback uniforme.
 */
const computeLayout = (milestones, today) => {
  const filled = milestones.filter((m) => m.date);
  if (filled.length < 2) {
    // fallback : positions équidistantes 0% / 33% / 66% / 100%
    return milestones.map((m, i) => ({ ...m, position: (i / (milestones.length - 1)) * 100 }));
  }
  const min = Math.min(...filled.map((m) => m.date.getTime()));
  const max = Math.max(...filled.map((m) => m.date.getTime()));
  const span = max - min;
  if (span === 0) {
    return milestones.map((m, i) => ({ ...m, position: (i / (milestones.length - 1)) * 100 }));
  }
  // Place chaque jalon proportionnellement à son écart au min
  return milestones.map((m) => {
    if (!m.date) return { ...m, position: null };
    return { ...m, position: ((m.date.getTime() - min) / span) * 100 };
  }).map((m, idx, arr) => {
    // Remplit les jalons sans date en interpolant entre voisins
    if (m.position !== null) return m;
    let prev = null, next = null;
    for (let i = idx - 1; i >= 0; i--) if (arr[i].position !== null) { prev = arr[i]; break; }
    for (let i = idx + 1; i < arr.length; i++) if (arr[i].position !== null) { next = arr[i]; break; }
    if (prev && next) return { ...m, position: (prev.position + next.position) / 2 };
    if (prev) return { ...m, position: Math.min(100, prev.position + 12) };
    if (next) return { ...m, position: Math.max(0, next.position - 12) };
    return { ...m, position: 50 };
  });
};

/**
 * Position du curseur "Aujourd'hui" sur la timeline.
 * - Avant le 1er jalon renseigné → 0% + état "à venir"
 * - Après le dernier jalon → 100% + état "achevé"
 * - Sinon : interpolation linéaire entre min/max dates
 */
const computeTodayCursor = (filledMilestones, today) => {
  if (filledMilestones.length < 2) return null;
  const min = Math.min(...filledMilestones.map((m) => m.date.getTime()));
  const max = Math.max(...filledMilestones.map((m) => m.date.getTime()));
  const t = today.getTime();
  if (t <= min) return { position: 0, state: 'upcoming' };
  if (t >= max) return { position: 100, state: 'completed' };
  return { position: ((t - min) / (max - min)) * 100, state: 'in_progress' };
};

/** Phase active : nom du jalon le plus récent passé OU prochain jalon à venir. */
const computePhase = (filledMilestones, today) => {
  if (filledMilestones.length === 0) return null;
  const past = filledMilestones.filter((m) => m.date.getTime() <= today.getTime());
  const future = filledMilestones.filter((m) => m.date.getTime() > today.getTime());
  if (past.length === 0) {
    return { label: 'Avant lancement', sub: `Prochain · ${future[0].label} ${fmtShort(future[0].date)}` };
  }
  if (future.length === 0) {
    return { label: 'Programme achevé', sub: `Livraison ${fmtShort(past[past.length - 1].date)}` };
  }
  return {
    label: `Phase actuelle · ${past[past.length - 1].label}`,
    sub: `Prochaine étape · ${future[0].label} ${fmtShort(future[0].date)}`,
  };
};

const ProjectTimeline = ({ project, onNavigateToCalendar }) => {
  const today = useMemo(() => new Date(), []);
  const [alertsMap, setAlertsMap] = useState({}); // {milestone_key: {alert,reason,action_label}}

  // Fetch alerts + sync notifications to the bell (one-shot at mount per project)
  useEffect(() => {
    const projectId = project?.project_id;
    if (!projectId) return;
    let cancelled = false;
    const run = async () => {
      try {
        const r = await axios.get(`${API}/projects/${projectId}/timeline-alerts`, opts);
        if (cancelled) return;
        const map = {};
        (r.data?.items || []).forEach((it) => { map[it.key] = it; });
        setAlertsMap(map);
        // Fire-and-forget sync to seed notifications (dedupes server-side)
        axios.post(`${API}/projects/${projectId}/timeline-alerts/sync`, {}, opts).catch(() => {});
      } catch { /* silent */ }
    };
    run();
    return () => { cancelled = true; };
  }, [project?.project_id]);

  const { milestones, todayCursor, phase, filledCount } = useMemo(() => {
    const generalInfo = project?.general_info || {};
    const milestonesRaw = MILESTONES.map((m) => ({
      ...m,
      date: parseDate(generalInfo[m.key]),
    }));
    const filled = milestonesRaw.filter((m) => m.date);
    const laidOut = computeLayout(milestonesRaw, today);
    return {
      milestones: laidOut,
      todayCursor: computeTodayCursor(filled, today),
      phase: computePhase(filled, today),
      filledCount: filled.length,
    };
  }, [project, today]);

  const alertsCount = useMemo(
    () => Object.values(alertsMap).filter((a) => a?.alert).length,
    [alertsMap]
  );

  // ─────────── Empty state : aucune date n'est renseignée ───────────
  if (filledCount === 0) {
    const handleEmptyCta = () => onNavigateToCalendar?.(null);
    return (
      <HudCard variant="glass" data-testid="project-timeline-empty">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            style={{ background: 'var(--c-surface-2)', border: '1px solid var(--c-border)' }}
          >
            <CircleDashed className="w-4 h-4" style={{ color: 'var(--c-text-muted)' }} />
          </div>
          <div className="flex-1">
            <span className="hud-eyebrow">Cycle de vie</span>
            <p className="text-[13px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
              Renseignez les dates clés (dépôt PC, ouverture chantier, commercialisation, livraison) dans <strong style={{ color: 'var(--c-text)' }}>Paramètres → Calendrier</strong> pour activer la mini-timeline.
            </p>
          </div>
          {onNavigateToCalendar && (
            <button
              type="button"
              onClick={handleEmptyCta}
              data-testid="timeline-empty-cta"
              className="text-[11.5px] font-semibold px-3 py-1.5 rounded-lg transition-colors shrink-0"
              style={{
                background: 'var(--c-gold-soft)',
                color: 'var(--c-gold)',
                border: '1px solid var(--c-border-accent)',
              }}
            >
              Compléter →
            </button>
          )}
        </div>
      </HudCard>
    );
  }

  return (
    <HudCard variant="glass" className="overflow-hidden" data-testid="project-timeline">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <span className="hud-eyebrow hud-eyebrow-gold">Cycle de vie</span>
          {phase && (
            <p
              className="mt-1 font-semibold tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif', color: 'var(--c-text)', fontSize: 16 }}
              data-testid="timeline-phase-label"
            >
              {phase.label}
            </p>
          )}
          {phase?.sub && (
            <p className="text-[12px] mt-0.5" style={{ color: 'var(--c-text-2)' }}>
              {phase.sub}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {alertsCount > 0 && (
            <span
              data-testid="timeline-alerts-badge"
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-[0.14em]"
              style={{
                background: 'rgba(239,68,68,0.14)',
                color: '#FCA5A5',
                border: '1px solid rgba(239,68,68,0.42)',
                boxShadow: '0 0 14px rgba(239,68,68,0.20)',
              }}
            >
              <AlertTriangle className="w-3 h-3" strokeWidth={2.4} />
              {alertsCount} alerte{alertsCount > 1 ? 's' : ''}
            </span>
          )}
          <div className="text-right">
            <span className="hud-eyebrow">Aujourd'hui</span>
            <p
              className="mt-1 text-[13px] font-medium tabular-nums"
              style={{ color: 'var(--c-text)' }}
              data-testid="timeline-today-label"
            >
              {today.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>
      </div>

      {/* Timeline rail */}
      <div className="relative pt-2 pb-12" data-testid="timeline-rail">
        {/* Rail background */}
        <div
          className="absolute left-0 right-0 h-[2px] rounded-full"
          style={{ top: 19, background: 'var(--c-border)' }}
        />
        {/* Progress bar (filled portion up to today) */}
        {todayCursor && todayCursor.position > 0 && (
          <div
            className="absolute left-0 h-[2px] rounded-full"
            style={{
              top: 19,
              width: `${todayCursor.position}%`,
              background: 'linear-gradient(90deg, var(--c-gold) 0%, var(--c-gold-soft) 100%)',
              boxShadow: '0 0 12px rgba(217,160,91,0.50)',
            }}
            data-testid="timeline-progress"
          />
        )}

        {/* Today cursor */}
        {todayCursor && (
          <div
            className="absolute -translate-x-1/2 flex flex-col items-center"
            style={{ left: `${todayCursor.position}%`, top: -2, zIndex: 4 }}
            data-testid="timeline-today-cursor"
          >
            <span
              className="text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-md mb-1 whitespace-nowrap"
              style={{
                background: 'var(--c-gold)',
                color: '#0E1116',
                boxShadow: '0 2px 10px rgba(217,160,91,0.45)',
              }}
            >
              Aujourd'hui
            </span>
            <span
              className="w-3 h-3 rounded-full"
              style={{
                background: 'var(--c-gold)',
                border: '2px solid var(--c-surface-1)',
                boxShadow: '0 0 0 4px rgba(217,160,91,0.18), 0 0 14px rgba(217,160,91,0.55)',
              }}
            />
          </div>
        )}

        {/* Milestones */}
        {milestones.map((m) => {
          if (m.position === null || m.position === undefined) return null;
          const Icon = m.icon;
          const isFilled = !!m.date;
          const isPast = isFilled && m.date.getTime() <= today.getTime();
          const alertInfo = alertsMap[m.key];
          const inAlert = !!alertInfo?.alert;

          // Color logic (alert takes precedence over "passé" gold)
          const ringColor = inAlert
            ? '#EF4444'
            : !isFilled
              ? 'rgba(255,255,255,0.10)'
              : isPast
                ? 'var(--c-gold)'
                : 'rgba(255,255,255,0.30)';
          const dotBg = inAlert
            ? '#EF4444'
            : !isFilled
              ? 'var(--c-surface-2)'
              : isPast
                ? 'var(--c-gold)'
                : 'var(--c-surface-2)';
          const iconColor = inAlert
            ? '#FFFFFF'
            : !isFilled
              ? 'var(--c-text-faint)'
              : isPast
                ? '#0E1116'
                : 'var(--c-text)';
          const labelColor = isFilled ? 'var(--c-text)' : 'var(--c-text-faint)';
          const subColor = isFilled ? 'var(--c-text-2)' : 'var(--c-text-faint)';

          // ── Tooltip text (delta jours / pluriel / passé-futur) ──
          let deltaText;
          if (!isFilled) {
            deltaText = 'Date non renseignée — cliquez pour la définir';
          } else {
            const diffMs = m.date.getTime() - today.getTime();
            const diffDays = Math.round(diffMs / 86400000);
            const abs = Math.abs(diffDays);
            const plural = abs > 1 ? 's' : '';
            if (diffDays === 0) {
              deltaText = `${m.label} : aujourd'hui !`;
            } else if (diffDays < 0) {
              // Passé : "+ X jours depuis Y"
              const verb = {
                date_depot_pc: 'le dépôt du PC',
                date_ouverture_chantier: 'l\'ouverture du chantier',
                date_commercialisation: 'le début de la commercialisation',
                date_livraison: 'la livraison',
              }[m.key] || m.label.toLowerCase();
              deltaText = `+ ${abs} jour${plural} depuis ${verb}`;
            } else {
              // Futur : "- X jours avant Y"
              const verb = {
                date_depot_pc: 'le dépôt du PC',
                date_ouverture_chantier: 'l\'ouverture du chantier',
                date_commercialisation: 'le début de la commercialisation',
                date_livraison: 'la livraison',
              }[m.key] || m.label.toLowerCase();
              deltaText = `− ${abs} jour${plural} avant ${verb}`;
            }
          }

          // Tooltip placement : flip à gauche si on est > 75% pour ne pas déborder
          const tooltipAlignRight = m.position > 75;

          return (
            <button
              key={m.key}
              type="button"
              onClick={() => onNavigateToCalendar?.(m.key)}
              className="group absolute -translate-x-1/2 flex flex-col items-center cursor-pointer focus:outline-none"
              style={{ left: `${m.position}%`, top: 12 }}
              data-testid={`timeline-milestone-${m.key}`}
              aria-label={`${m.label}${m.date ? ` · ${fmtLong(m.date)}` : ' · non renseigné'}. Cliquez pour modifier.`}
            >
              {/* Dot */}
              <span
                className="w-4 h-4 rounded-full flex items-center justify-center transition-transform group-hover:scale-125 group-focus-visible:scale-125 relative"
                style={{
                  background: dotBg,
                  border: `2px solid ${ringColor}`,
                  boxShadow: inAlert
                    ? '0 0 14px rgba(239,68,68,0.55), 0 0 0 4px rgba(239,68,68,0.16)'
                    : (isPast ? '0 0 14px rgba(217,160,91,0.45)' : 'none'),
                  opacity: isFilled ? 1 : 0.55,
                  animation: inAlert ? 'hud-pulse-alert 1.6s ease-in-out infinite' : undefined,
                }}
              >
                <Icon className="w-2 h-2" strokeWidth={2.5} style={{ color: iconColor }} />
                {inAlert && (
                  <span
                    aria-hidden
                    className="absolute -top-1.5 -right-1.5 w-3 h-3 rounded-full flex items-center justify-center"
                    style={{
                      background: '#FEE2E2',
                      border: '1.5px solid #EF4444',
                      boxShadow: '0 0 8px rgba(239,68,68,0.7)',
                    }}
                  >
                    <AlertTriangle className="w-1.5 h-1.5" strokeWidth={3} style={{ color: '#B91C1C' }} />
                  </span>
                )}
              </span>
              {/* Connector */}
              <span className="h-2 w-px" style={{ background: 'var(--c-border)' }} aria-hidden />
              {/* Label */}
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.12em] whitespace-nowrap mt-0.5 transition-colors group-hover:text-[color:var(--c-gold)]"
                style={{ color: labelColor, opacity: isFilled ? 1 : 0.6 }}
              >
                {m.shortLabel}
              </span>
              <span
                className="text-[10.5px] tabular-nums whitespace-nowrap mt-0.5"
                style={{ color: subColor, opacity: isFilled ? 1 : 0.5 }}
              >
                {isFilled ? fmtShort(m.date) : '—'}
              </span>

              {/* Tooltip rich */}
              <span
                role="tooltip"
                data-testid={`timeline-tooltip-${m.key}`}
                className="pointer-events-none absolute opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity duration-150"
                style={{
                  bottom: 'calc(100% + 14px)',
                  left: tooltipAlignRight ? 'auto' : '50%',
                  right: tooltipAlignRight ? '50%' : 'auto',
                  transform: tooltipAlignRight ? 'translateX(50%)' : 'translateX(-50%)',
                  zIndex: 20,
                  minWidth: 188,
                  maxWidth: 240,
                  background: 'rgba(14,17,22,0.96)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid var(--c-border)',
                  borderRadius: 10,
                  padding: '10px 12px',
                  boxShadow: '0 14px 36px -14px rgba(0,0,0,0.8), 0 0 0 1px rgba(217,160,91,0.10)',
                }}
              >
                <span
                  className="block text-[9px] font-bold uppercase tracking-[0.18em] mb-0.5"
                  style={{ color: 'var(--c-gold)' }}
                >
                  {m.label}
                </span>
                <span
                  className="block text-[12px] font-semibold mb-1"
                  style={{ color: 'var(--c-text)', fontFamily: 'Outfit, sans-serif' }}
                >
                  {fmtLong(m.date) || 'À définir'}
                </span>
                <span
                  className="block text-[11px] leading-snug"
                  style={{
                    color: inAlert
                      ? '#FCA5A5'
                      : isFilled
                        ? (isPast ? 'rgba(134,239,172,0.95)' : 'rgba(253,224,71,0.95)')
                        : 'var(--c-text-2)',
                  }}
                >
                  {deltaText}
                </span>
                {inAlert && alertInfo?.reason && (
                  <span
                    className="block text-[10.5px] mt-1.5 pt-1.5 leading-snug"
                    style={{
                      color: '#FCA5A5',
                      borderTop: '1px dashed rgba(239,68,68,0.30)',
                    }}
                    data-testid={`timeline-alert-reason-${m.key}`}
                  >
                    <strong style={{ color: '#FECACA' }}>⚠ Action requise :</strong> {alertInfo.reason}
                  </span>
                )}
                <span
                  className="block text-[10px] mt-1.5 pt-1.5"
                  style={{ color: 'var(--c-text-faint)', borderTop: '1px dashed var(--c-border)' }}
                >
                  Cliquer pour modifier →
                </span>
                {/* Caret */}
                <span
                  aria-hidden
                  className="absolute w-2 h-2 rotate-45"
                  style={{
                    bottom: -5,
                    left: tooltipAlignRight ? 'auto' : '50%',
                    right: tooltipAlignRight ? '50%' : 'auto',
                    transform: tooltipAlignRight ? 'translateX(50%) rotate(45deg)' : 'translateX(-50%) rotate(45deg)',
                    background: 'rgba(14,17,22,0.96)',
                    borderRight: '1px solid var(--c-border)',
                    borderBottom: '1px solid var(--c-border)',
                  }}
                />
              </span>
            </button>
          );
        })}
      </div>

      {/* Footer legend */}
      <div className="flex items-center gap-4 flex-wrap pt-3" style={{ borderTop: '1px dashed var(--c-border)' }}>
        <LegendDot color="var(--c-gold)" label="Jalon franchi" testid="timeline-legend-past" />
        <LegendDot color="rgba(255,255,255,0.35)" label="Jalon à venir" testid="timeline-legend-future" />
        <LegendDot color="rgba(255,255,255,0.10)" label="Date non renseignée" testid="timeline-legend-empty" />
        {alertsCount > 0 && (
          <LegendDot color="#EF4444" label="Action requise" testid="timeline-legend-alert" />
        )}
        {filledCount < 4 && (
          <span className="text-[11px] ml-auto" style={{ color: 'var(--c-text-faint)' }}>
            {filledCount}/4 dates renseignées · complétez dans <strong style={{ color: 'var(--c-text-2)' }}>Paramètres</strong>
          </span>
        )}
      </div>

      {/* Local keyframes for alert pulse */}
      <style>{`
        @keyframes hud-pulse-alert {
          0%, 100% { box-shadow: 0 0 14px rgba(239,68,68,0.55), 0 0 0 4px rgba(239,68,68,0.16); }
          50%      { box-shadow: 0 0 20px rgba(239,68,68,0.80), 0 0 0 6px rgba(239,68,68,0.10); }
        }
      `}</style>
    </HudCard>
  );
};

const LegendDot = ({ color, label, testid }) => (
  <span className="flex items-center gap-1.5 text-[11px]" data-testid={testid} style={{ color: 'var(--c-text-2)' }}>
    <span
      className="w-2 h-2 rounded-full"
      style={{ background: color, border: '1px solid rgba(255,255,255,0.15)' }}
    />
    {label}
  </span>
);

export default ProjectTimeline;
